import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Trash2, Plus, RotateCcw, Save, Eye, ArrowLeft } from 'lucide-react';

const AGREEMENT_TYPES = [
  { key: 'new_memorial', label: 'New Memorial', signable: true },
  { key: 'additional_inscription', label: 'Additional Inscription', signable: true },
  { key: 'restoration', label: 'Restoration / Cleaning / Re-level', signable: true },
  { key: 'full_terms', label: 'Full Terms & Conditions', signable: false },
];

const VARIABLES = [
  'company_name', 'customer_name', 'order_number',
  'deposit_percentage', 'estimated_lead_time',
  'memorial_warranty_years', 'lettering_warranty_years',
  'fixing_warranty_years', 'insurance_years',
  'terms_url',
];

export default function AgreementsSettings() {
  const { token, API } = useContext(AuthContext);
  const navigate = useNavigate();
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState({});
  const [activeType, setActiveType] = useState('new_memorial');
  const [preview, setPreview] = useState('');
  const [dirty, setDirty] = useState({});  // {type: bool}

  const loadAll = useCallback(async () => {
    try {
      const [s, t] = await Promise.all([
        axios.get(`${API}/agreements/settings`, auth),
        axios.get(`${API}/agreements/templates`, auth),
      ]);
      setSettings(s.data);
      // Extract just the `current` template per type
      const map = {};
      for (const k of Object.keys(t.data)) map[k] = { ...t.data[k].current, _history: t.data[k].history };
      setTemplates(map);
    } catch (e) {
      toast.error('Failed to load agreement settings');
    }
  }, [API, token]);  // eslint-disable-line

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveSettings = async (patch) => {
    try {
      const { data } = await axios.put(`${API}/agreements/settings`, patch, auth);
      setSettings(data);
      toast.success('Settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    }
  };

  const updateTemplate = (type, patch) => {
    setTemplates(t => ({ ...t, [type]: { ...t[type], ...patch } }));
    setDirty(d => ({ ...d, [type]: true }));
  };

  const saveTemplate = async (type) => {
    const t = templates[type];
    try {
      const { data } = await axios.put(`${API}/agreements/templates/${type}`, {
        title: t.title,
        body: t.body,
        acknowledgements: (t.acknowledgements || []).map(a => ({ id: a.id, text: a.text, required: !!a.required })),
      }, auth);
      setTemplates(prev => ({ ...prev, [type]: { ...data, _history: prev[type]._history } }));
      setDirty(d => ({ ...d, [type]: false }));
      toast.success(`Saved as version ${data.version}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const restoreDefault = async (type) => {
    if (!window.confirm('This creates a NEW version populated from the Memorial Desk default. Existing signed agreements are not affected. Continue?')) return;
    try {
      const { data } = await axios.post(`${API}/agreements/templates/${type}/restore-default`, {}, auth);
      setTemplates(prev => ({ ...prev, [type]: { ...data, _history: prev[type]._history } }));
      setDirty(d => ({ ...d, [type]: false }));
      toast.success(`Restored to default (new version ${data.version})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
    }
  };

  const previewTemplate = async (type) => {
    const t = templates[type];
    try {
      const { data } = await axios.post(`${API}/agreements/templates/${type}/preview`,
        { body: t.body }, auth);
      setPreview(data.rendered);
    } catch (e) {
      setPreview('Preview failed');
    }
  };

  const insertVariable = (type, varName) => {
    const t = templates[type];
    updateTemplate(type, { body: (t.body || '') + `{{${varName}}}` });
  };

  const addAck = (type) => {
    const t = templates[type];
    const acks = [...(t.acknowledgements || [])];
    acks.push({ id: `ack${acks.length + 1}`, text: '', required: true });
    updateTemplate(type, { acknowledgements: acks });
  };
  const removeAck = (type, idx) => {
    const acks = [...(templates[type].acknowledgements || [])];
    acks.splice(idx, 1);
    updateTemplate(type, { acknowledgements: acks });
  };
  const updateAck = (type, idx, patch) => {
    const acks = [...(templates[type].acknowledgements || [])];
    acks[idx] = { ...acks[idx], ...patch };
    updateTemplate(type, { acknowledgements: acks });
  };

  if (!settings) return <div className="p-6">Loading…</div>;

  const wc = settings.warranty_config || {};

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
        </Button>
        <span className="text-slate-300">·</span>
        <Button variant="ghost" size="sm" onClick={() => navigate('/settings')} data-testid="back-to-settings">
          Settings
        </Button>
      </div>
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Terms & Customer Agreements</h1>
        <p className="text-slate-500 text-sm mt-1">Configure the digital agreements customers sign before their order progresses.</p>
      </div>

      <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="admin-disclaimer">
        <strong>Admin note:</strong> Memorial Desk provides these templates as a starting point only. You are responsible for ensuring that your customer agreements, warranties, cancellation terms and Terms & Conditions are appropriate for your business and comply with applicable law.
      </div>

      {/* MASTER SWITCH */}
      <Card>
        <CardHeader><CardTitle>Master Switch</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">Use customer agreements</Label>
              <p className="text-xs text-slate-500">When off, no agreement stage is shown. Orders continue through the existing workflow normally.</p>
            </div>
            <Switch checked={!!settings.use_customer_agreements}
                    onCheckedChange={v => saveSettings({ use_customer_agreements: v })}
                    data-testid="master-switch" />
          </div>

          {settings.use_customer_agreements && (
            <div className="grid gap-2 pl-4 border-l-2 border-slate-200">
              <ToggleRow label="New Memorial" checked={settings.enable_new_memorial}
                         onChange={v => saveSettings({ enable_new_memorial: v })}
                         testid="toggle-new-memorial" />
              <ToggleRow label="Additional Inscription" checked={settings.enable_additional_inscription}
                         onChange={v => saveSettings({ enable_additional_inscription: v })}
                         testid="toggle-additional-inscription" />
              <ToggleRow label="Restoration / Cleaning / Re-level" checked={settings.enable_restoration}
                         onChange={v => saveSettings({ enable_restoration: v })}
                         testid="toggle-restoration" />
              <div className="border-t pt-2 mt-2">
                <ToggleRow label="Require agreement before accepting deposit"
                           checked={settings.require_agreement_before_deposit}
                           onChange={v => saveSettings({ require_agreement_before_deposit: v })}
                           testid="toggle-require-before-deposit" />
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <ToggleRow label="Allow drawn signature" checked={settings.allow_drawn_signature}
                           onChange={v => saveSettings({ allow_drawn_signature: v })}
                           testid="toggle-drawn-sig" />
                <ToggleRow label="Allow typed signature" checked={settings.allow_typed_signature}
                           onChange={v => saveSettings({ allow_typed_signature: v })}
                           testid="toggle-typed-sig" />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* WARRANTY CONFIG */}
      <Card>
        <CardHeader>
          <CardTitle>Warranty & Deposit Configuration</CardTitle>
          <CardDescription>Structured values that plug into <code>{'{{variables}}'}</code> in your templates — and later, customer warranty certificates.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <WarrantyField label="Deposit %" k="deposit_percentage" val={wc.deposit_percentage} onSave={saveSettings} />
            <WarrantyField label="Estimated lead time" k="estimated_lead_time" val={wc.estimated_lead_time} onSave={saveSettings} isText />
            <WarrantyField label="Memorial warranty (years)" k="memorial_warranty_years" val={wc.memorial_warranty_years} onSave={saveSettings} />
            <WarrantyField label="Lettering warranty (years)" k="lettering_warranty_years" val={wc.lettering_warranty_years} onSave={saveSettings} />
            <WarrantyField label="Fixing warranty (years)" k="fixing_warranty_years" val={wc.fixing_warranty_years} onSave={saveSettings} />
            <WarrantyField label="Insurance cover (years)" k="insurance_years" val={wc.insurance_years} onSave={saveSettings} />
          </div>
        </CardContent>
      </Card>

      {/* FULL T&C MODE */}
      <Card>
        <CardHeader><CardTitle>Full Terms & Conditions</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2">
              <input type="radio" name="ft_mode" checked={settings.full_terms_mode === 'template'}
                     onChange={() => saveSettings({ full_terms_mode: 'template' })}
                     data-testid="ft-mode-template" />
              Use Memorial Desk editable template (edit under the &ldquo;Full Terms&rdquo; tab below)
            </label>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2">
              <input type="radio" name="ft_mode" checked={settings.full_terms_mode === 'external_url'}
                     onChange={() => saveSettings({ full_terms_mode: 'external_url' })}
                     data-testid="ft-mode-external" />
              Link to my own T&C URL:
            </label>
            <Input value={settings.external_terms_url || ''} placeholder="https://your-site.co.uk/terms"
                   onChange={e => setSettings(s => ({ ...s, external_terms_url: e.target.value }))}
                   onBlur={() => saveSettings({ external_terms_url: settings.external_terms_url || '' })}
                   className="max-w-md" data-testid="ft-external-url" />
          </div>
        </CardContent>
      </Card>

      {/* TEMPLATE EDITOR */}
      <Card>
        <CardHeader>
          <CardTitle>Template Editor</CardTitle>
          <CardDescription>Each save creates a new version. Signed agreements always keep the exact wording they were signed with.</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeType} onValueChange={setActiveType}>
            <TabsList className="grid grid-cols-4 mb-4">
              {AGREEMENT_TYPES.map(t => (
                <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key}`}>{t.label}</TabsTrigger>
              ))}
            </TabsList>
            {AGREEMENT_TYPES.map(t => {
              const tpl = templates[t.key] || {};
              return (
                <TabsContent key={t.key} value={t.key} className="space-y-4">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-sm text-slate-500">
                      Current version: <strong>{tpl.version || 0}</strong>
                      {tpl.is_default && ' (Memorial Desk default)'}
                      {tpl.created_at && ` · saved ${new Date(tpl.created_at).toLocaleString('en-GB')}`}
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => previewTemplate(t.key)} data-testid={`preview-${t.key}`}>
                        <Eye className="w-4 h-4 mr-1" /> Preview
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => restoreDefault(t.key)} data-testid={`restore-${t.key}`}>
                        <RotateCcw className="w-4 h-4 mr-1" /> Restore Default
                      </Button>
                      <Button size="sm" disabled={!dirty[t.key]} onClick={() => saveTemplate(t.key)}
                              className="bg-slate-900 hover:bg-slate-800" data-testid={`save-${t.key}`}>
                        <Save className="w-4 h-4 mr-1" /> Save New Version
                      </Button>
                    </div>
                  </div>

                  <div>
                    <Label>Title</Label>
                    <Input value={tpl.title || ''} onChange={e => updateTemplate(t.key, { title: e.target.value })}
                           data-testid={`title-${t.key}`} />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label>Agreement Body</Label>
                      <div className="flex flex-wrap gap-1">
                        {VARIABLES.map(v => (
                          <button key={v} type="button" onClick={() => insertVariable(t.key, v)}
                                  className="text-xs px-2 py-0.5 bg-slate-100 hover:bg-slate-200 rounded font-mono"
                                  data-testid={`var-${t.key}-${v}`}>{`{{${v}}}`}</button>
                        ))}
                      </div>
                    </div>
                    <Textarea rows={12} value={tpl.body || ''} onChange={e => updateTemplate(t.key, { body: e.target.value })}
                              className="font-mono text-sm" data-testid={`body-${t.key}`} />
                  </div>

                  {t.signable && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label>Required Tick-Boxes (shown before signature)</Label>
                        <Button size="sm" variant="outline" onClick={() => addAck(t.key)} data-testid={`add-ack-${t.key}`}>
                          <Plus className="w-4 h-4 mr-1" /> Add tick-box
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {(tpl.acknowledgements || []).map((a, idx) => (
                          <div key={a.id || idx} className="flex items-start gap-2 border rounded p-2 bg-slate-50"
                               data-testid={`ack-row-${t.key}-${idx}`}>
                            <div className="flex flex-col items-center pt-1 min-w-[70px]">
                              <label className="flex items-center gap-1 text-xs">
                                <input type="checkbox" checked={!!a.required}
                                       onChange={e => updateAck(t.key, idx, { required: e.target.checked })} />
                                required
                              </label>
                            </div>
                            <Textarea rows={2} value={a.text} onChange={e => updateAck(t.key, idx, { text: e.target.value })}
                                      className="flex-1 text-sm" />
                            <Button size="sm" variant="ghost" onClick={() => removeAck(t.key, idx)}
                                    className="text-red-600 hover:bg-red-50" data-testid={`remove-ack-${t.key}-${idx}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                        {(tpl.acknowledgements || []).length === 0 && (
                          <p className="text-xs text-slate-500">No tick-boxes yet — customers can sign without any explicit acknowledgement.</p>
                        )}
                      </div>
                    </div>
                  )}
                </TabsContent>
              );
            })}
          </Tabs>

          {preview && (
            <div className="mt-4 border rounded p-4 bg-white" data-testid="preview-panel">
              <div className="flex items-center justify-between mb-2">
                <strong>Preview (variables substituted)</strong>
                <button className="text-xs text-slate-500 hover:text-slate-800" onClick={() => setPreview('')}>Close</button>
              </div>
              <pre className="whitespace-pre-wrap text-sm text-slate-700">{preview}</pre>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ToggleRow({ label, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between">
      <Label className="text-sm">{label}</Label>
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

function WarrantyField({ label, k, val, onSave, isText }) {
  const [local, setLocal] = useState(val ?? '');
  useEffect(() => { setLocal(val ?? ''); }, [val]);
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input type={isText ? 'text' : 'number'} value={local}
             onChange={e => setLocal(e.target.value)}
             onBlur={() => {
               const parsed = isText ? local : (Number(local) || 0);
               if (parsed !== val) onSave({ warranty_config: { [k]: parsed } });
             }}
             data-testid={`warranty-${k}`} />
    </div>
  );
}
